import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, MessageCircle, Edit2, Save, Loader2, AlertTriangle, Plus, FileSignature, Calendar, ChevronRight, Search, ArrowUpDown } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import BookingDetailModal from "../../components/BookingDetailModal";

const RequestsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Search and sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  
  // Booking Detail Modal state
  const [bookingDetailOpen, setBookingDetailOpen] = useState(false);
  
  // Action Dialog state (confirm/decline/complete)
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [alternativeDates, setAlternativeDates] = useState("");
  const [overlapWarning, setOverlapWarning] = useState(null);
  
  // Edit Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Create Booking Dialog state
  const [createBookingDialogOpen, setCreateBookingDialogOpen] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    event_type: "portrait",
    event_date: "",
    start_time: "",
    end_time: "",
    venue_name: "",
    venue_address: "",
    request_types: ["photography"],
    notes: ""
  });
  
  // Attach Contract Dialog state
  const [attachContractDialogOpen, setAttachContractDialogOpen] = useState(false);
  const [availableContracts, setAvailableContracts] = useState([]);
  const [selectedContractIds, setSelectedContractIds] = useState([]);
  const [attachingContract, setAttachingContract] = useState(false);

  // Handle URL params for direct navigation from notifications
  useEffect(() => {
    const bookingId = searchParams.get("booking");
    const tab = searchParams.get("tab");
    
    if (bookingId && requests.length > 0) {
      const request = requests.find(r => r.request_id === bookingId);
      if (request) {
        setSelectedRequest(request);
        setBookingDetailOpen(true);
        // Clear URL params after handling
        setSearchParams({});
      }
    }
  }, [searchParams, requests]);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/bookings/requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const checkForOverlap = async (request) => {
    try {
      const response = await axios.post(
        `${API}/availability/check-overlap`,
        {
          event_date: request.event_date,
          start_time: request.start_time,
          end_time: request.end_time,
          exclude_request_id: request.request_id
        },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to check overlap:", error);
      return { has_overlap: false, overlapping_bookings: [] };
    }
  };

  const openActionDialog = async (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    
    // For confirm action, check for overlaps
    if (type === "confirmed" && request.event_date) {
      const overlap = await checkForOverlap(request);
      if (overlap.has_overlap) {
        setOverlapWarning(overlap.overlapping_bookings);
      } else {
        setOverlapWarning(null);
      }
    } else {
      setOverlapWarning(null);
    }
    
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;

    try {
      await axios.put(
        `${API}/bookings/request/${selectedRequest.request_id}`,
        {
          status: actionType,
          decline_reason: declineReason,
          alternative_dates: alternativeDates
        },
        { withCredentials: true }
      );

      toast.success(`Request ${actionType === "confirmed" ? "confirmed" : "updated"}!`);
      setActionDialogOpen(false);
      setDeclineReason("");
      setAlternativeDates("");
      setOverlapWarning(null);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  const openEditDialog = (request) => {
    setSelectedRequest(request);
    setEditData({
      status: request.status || "pending",
      customer_name: request.customer_name || "",
      customer_email: request.customer_email || "",
      customer_phone: request.customer_phone || "",
      customer_instagram: request.customer_instagram || "",
      event_type: request.event_type || "",
      event_date: request.event_date || "",
      start_time: request.start_time || "",
      end_time: request.end_time || "",
      venue_name: request.venue_name || "",
      venue_address: request.venue_address || "",
      team_name: request.team_name || "",
      jersey_numbers: request.jersey_numbers || "",
      is_tournament: request.is_tournament || false,
      tournament_start_date: request.tournament_start_date || "",
      tournament_end_date: request.tournament_end_date || "",
      notes: request.notes || ""
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedRequest) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/bookings/request/${selectedRequest.request_id}/edit`,
        editData,
        { withCredentials: true }
      );
      toast.success("Request updated!");
      setEditDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update request");
    } finally {
      setSaving(false);
    }
  };

  const createBooking = async () => {
    // Validate required fields
    if (!newBooking.customer_name || !newBooking.customer_email) {
      toast.error("Please enter client name and email");
      return;
    }
    if (!newBooking.event_type) {
      toast.error("Please select an event type");
      return;
    }

    setCreatingBooking(true);
    try {
      await axios.post(
        `${API}/bookings/request/manual`,
        newBooking,
        { withCredentials: true }
      );
      
      toast.success("Booking created successfully!");
      setCreateBookingDialogOpen(false);
      setNewBooking({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        event_type: "portrait",
        event_date: "",
        start_time: "",
        end_time: "",
        venue_name: "",
        venue_address: "",
        request_types: ["photography"],
        notes: ""
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setCreatingBooking(false);
    }
  };

  const openAttachContractDialog = async (request) => {
    setSelectedRequest(request);
    setSelectedContractIds(request.contract_ids || []);
    setAttachContractDialogOpen(true);
    
    // Fetch available contracts
    try {
      const response = await axios.get(`${API}/contracts`, { withCredentials: true });
      setAvailableContracts(response.data || []);
    } catch (error) {
      toast.error("Failed to load contracts");
    }
  };

  const toggleContractSelection = (contractId) => {
    setSelectedContractIds(prev => 
      prev.includes(contractId)
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const attachContractsToBooking = async () => {
    if (!selectedRequest) return;
    
    setAttachingContract(true);
    try {
      await axios.post(
        `${API}/bookings/request/${selectedRequest.request_id}/attach-contracts`,
        { contract_ids: selectedContractIds },
        { withCredentials: true }
      );
      
      toast.success("Contracts attached successfully!");
      setAttachContractDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to attach contracts");
    } finally {
      setAttachingContract(false);
    }
  };

  const handleRowClick = (request) => {
    setSelectedRequest(request);
    setBookingDetailOpen(true);
  };

  const handleStatusChange = (request, status) => {
    openActionDialog(request, status);
    setBookingDetailOpen(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      needs_info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      in_discussion: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      quote_sent: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      quote_declined: "bg-red-500/20 text-red-400 border-red-500/30",
      deposit_paid: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
    };
    return styles[status] || styles.pending;
  };

  // Filter and sort requests
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    // Apply search filter (case-insensitive partial match)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(r => 
        (r.customer_name && r.customer_name.toLowerCase().includes(query)) ||
        (r.customer_email && r.customer_email.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "oldest":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "event_soonest":
          // Put items without event_date at the end
          if (!a.event_date && !b.event_date) return 0;
          if (!a.event_date) return 1;
          if (!b.event_date) return -1;
          return new Date(a.event_date) - new Date(b.event_date);
        case "event_latest":
          // Put items without event_date at the end
          if (!a.event_date && !b.event_date) return 0;
          if (!a.event_date) return 1;
          if (!b.event_date) return -1;
          return new Date(b.event_date) - new Date(a.event_date);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [requests, statusFilter, searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="requests-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Booking Requests</h1>
          <p className="text-muted-foreground">Manage incoming booking requests</p>
        </div>
        
        <Button 
          onClick={() => setCreateBookingDialogOpen(true)} 
          className="btn-primary"
          data-testid="create-booking-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Booking
        </Button>
      </div>
      
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by client name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-requests"
          />
        </div>
        
        {/* Sort Dropdown */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48" data-testid="sort-requests">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="event_soonest">Event Date (Soonest)</SelectItem>
            <SelectItem value="event_latest">Event Date (Latest)</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_discussion">In Discussion</SelectItem>
            <SelectItem value="quote_sent">Quote Sent</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredRequests.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg mb-2">No requests found</p>
            <p className="text-muted-foreground">
              {searchQuery 
                ? `No results for "${searchQuery}"`
                : statusFilter === "all" 
                  ? "Booking requests will appear here"
                  : `No ${statusFilter.replace(/_/g, " ")} requests`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map((request, index) => (
            <motion.div
              key={request.request_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <Card 
                className="bg-card border-border hover:border-primary/30 transition-all cursor-pointer group"
                onClick={() => handleRowClick(request)}
                data-testid={`booking-row-${request.request_id}`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-4">
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-serif truncate">{request.customer_name}</h3>
                        <Badge className={`${getStatusBadge(request.status)} border shrink-0`}>
                          {request.status?.replace(/_/g, " ")}
                        </Badge>
                        {request.contract_ids?.length > 0 && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <FileSignature className="w-3 h-3 mr-1" />
                            {request.contract_ids.length}
                          </Badge>
                        )}
                        {request.google_calendar_event_id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Calendar className="w-4 h-4 text-green-500 shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Synced to Google Calendar</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span>{request.event_type}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="hidden sm:inline">{request.request_types?.join(", ")}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>
                          {request.is_tournament 
                            ? `${request.tournament_start_date} - ${request.tournament_end_date}`
                            : request.event_date || "Date TBD"}
                        </span>
                      </div>
                    </div>

                    {/* Arrow indicator */}
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Booking Detail Modal */}
      <BookingDetailModal
        open={bookingDetailOpen}
        onOpenChange={setBookingDetailOpen}
        request={selectedRequest}
        onRefresh={fetchRequests}
        onEditBooking={(req) => {
          setBookingDetailOpen(false);
          openEditDialog(req);
        }}
        onAttachContracts={(req) => {
          setBookingDetailOpen(false);
          openAttachContractDialog(req);
        }}
        onStatusChange={handleStatusChange}
      />

      {/* Action Dialog (Confirm/Decline/Complete) */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {actionType === "confirmed" && "Confirm Booking"}
              {actionType === "declined" && "Decline Booking"}
              {actionType === "completed" && "Mark as Completed"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {actionType === "confirmed" && (
              <>
                <p className="text-muted-foreground">
                  The customer will receive a confirmation email with booking details.
                </p>
                
                {/* Overlap Warning */}
                {overlapWarning && overlapWarning.length > 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-400">Scheduling Conflict</p>
                        <p className="text-sm text-muted-foreground mb-2">
                          This booking overlaps with existing confirmed bookings:
                        </p>
                        <ul className="space-y-1">
                          {overlapWarning.map((booking) => (
                            <li key={booking.request_id} className="text-sm">
                              <span className="font-medium">{booking.customer_name}</span>
                              <span className="text-muted-foreground">
                                {" - "}{booking.event_type}
                                {booking.start_time && ` (${booking.start_time} - ${booking.end_time})`}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-sm text-yellow-400 mt-2">
                          Are you sure you want to confirm this booking?
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {actionType === "declined" && (
              <>
                <div>
                  <Label>Reason (optional)</Label>
                  <Textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Explain why you're declining..."
                    className="mt-2"
                    data-testid="decline-reason"
                  />
                </div>
                <div>
                  <Label>Alternative Dates (optional)</Label>
                  <Textarea
                    value={alternativeDates}
                    onChange={(e) => setAlternativeDates(e.target.value)}
                    placeholder="Suggest alternative dates..."
                    className="mt-2"
                    data-testid="alternative-dates"
                  />
                </div>
              </>
            )}
            
            {actionType === "completed" && (
              <p className="text-muted-foreground">
                This will mark the booking as completed. You can then create a gallery for this event.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAction} data-testid="confirm-action">
              {actionType === "confirmed" && "Confirm Booking"}
              {actionType === "declined" && "Decline Booking"}
              {actionType === "completed" && "Mark Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Booking Request</DialogTitle>
            <DialogDescription>
              Update the booking request details. Changes will be saved to the database.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Status */}
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <Label className="mb-2 block font-medium">Request Status</Label>
              <Select 
                value={editData.status || "pending"}
                onValueChange={(value) => setEditData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="needs_info">Needs Info</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Info */}
            <div>
              <h4 className="text-sm font-medium mb-3">Customer Information</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Name</Label>
                  <Input
                    value={editData.customer_name || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, customer_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={editData.customer_email || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, customer_email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Phone</Label>
                  <Input
                    value={editData.customer_phone || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Instagram</Label>
                  <Input
                    value={editData.customer_instagram || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, customer_instagram: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Event Info */}
            <div>
              <h4 className="text-sm font-medium mb-3">Event Information</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Event Type</Label>
                  <Select 
                    value={editData.event_type || ""}
                    onValueChange={(value) => setEditData(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Event Date</Label>
                  <Input
                    type="date"
                    value={editData.event_date || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, event_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Start Time</Label>
                  <Input
                    type="time"
                    value={editData.start_time || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">End Time</Label>
                  <Input
                    type="time"
                    value={editData.end_time || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Venue Info */}
            <div>
              <h4 className="text-sm font-medium mb-3">Venue</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Venue Name</Label>
                  <Input
                    value={editData.venue_name || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, venue_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Address</Label>
                  <Input
                    value={editData.venue_address || ""}
                    onChange={(e) => setEditData(prev => ({ ...prev, venue_address: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Team Info (conditional) */}
            {(editData.event_type === "sports" || editData.team_name) && (
              <div>
                <h4 className="text-sm font-medium mb-3">Team Information</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Team Name</Label>
                    <Input
                      value={editData.team_name || ""}
                      onChange={(e) => setEditData(prev => ({ ...prev, team_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Jersey Numbers</Label>
                    <Input
                      value={editData.jersey_numbers || ""}
                      onChange={(e) => setEditData(prev => ({ ...prev, jersey_numbers: e.target.value }))}
                      placeholder="e.g., 10, 23, 45"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="mb-2 block">Notes</Label>
              <Textarea
                value={editData.notes || ""}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Booking Dialog */}
      <Dialog open={createBookingDialogOpen} onOpenChange={setCreateBookingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Create New Booking</DialogTitle>
            <DialogDescription>
              Add a booking on behalf of a client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Client Information */}
            <div>
              <h4 className="text-sm font-medium mb-3">Client Information</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    placeholder="Client name"
                    value={newBooking.customer_name}
                    onChange={(e) => setNewBooking({ ...newBooking, customer_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="client@email.com"
                    value={newBooking.customer_email}
                    onChange={(e) => setNewBooking({ ...newBooking, customer_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Phone (optional)</Label>
                  <Input
                    placeholder="Phone number"
                    value={newBooking.customer_phone}
                    onChange={(e) => setNewBooking({ ...newBooking, customer_phone: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div>
              <h4 className="text-sm font-medium mb-3">Event Details</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Type *</Label>
                  <Select 
                    value={newBooking.event_type}
                    onValueChange={(value) => setNewBooking({ ...newBooking, event_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="headshot">Headshot</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Event Date</Label>
                  <Input
                    type="date"
                    value={newBooking.event_date}
                    onChange={(e) => setNewBooking({ ...newBooking, event_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newBooking.start_time}
                    onChange={(e) => setNewBooking({ ...newBooking, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={newBooking.end_time}
                    onChange={(e) => setNewBooking({ ...newBooking, end_time: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-sm font-medium mb-3">Location</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Venue Name</Label>
                  <Input
                    placeholder="Venue name"
                    value={newBooking.venue_name}
                    onChange={(e) => setNewBooking({ ...newBooking, venue_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    placeholder="Address"
                    value={newBooking.venue_address}
                    onChange={(e) => setNewBooking({ ...newBooking, venue_address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes about this booking..."
                value={newBooking.notes}
                onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateBookingDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createBooking} disabled={creatingBooking}>
              {creatingBooking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach Contract Dialog */}
      <Dialog open={attachContractDialogOpen} onOpenChange={setAttachContractDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Attach Contracts
            </DialogTitle>
            <DialogDescription>
              Link contracts to {selectedRequest?.customer_name}&apos;s booking
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {availableContracts.length === 0 ? (
              <div className="text-center py-8">
                <FileSignature className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No contracts available</p>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setAttachContractDialogOpen(false);
                    window.location.href = "/dashboard/contracts";
                  }}
                >
                  Create Contract
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {availableContracts.map((contract) => (
                  <div 
                    key={contract.contract_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedContractIds.includes(contract.contract_id)
                        ? "border-primary/50 bg-primary/5"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                    onClick={() => toggleContractSelection(contract.contract_id)}
                  >
                    <Checkbox 
                      checked={selectedContractIds.includes(contract.contract_id)}
                      onCheckedChange={() => toggleContractSelection(contract.contract_id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{contract.template_name}</p>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            contract.status === "signed" 
                              ? "text-green-400 border-green-400/30" 
                              : "text-yellow-400 border-yellow-400/30"
                          }`}
                        >
                          {contract.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {contract.client_name} • {new Date(contract.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachContractDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={attachContractsToBooking} 
              disabled={attachingContract || availableContracts.length === 0}
            >
              {attachingContract ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save ({selectedContractIds.length} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestsPage;
